<script setup lang="ts">
/**
 * "Set a new password" (token from the reset email).
 *
 * FE-014 — redesign primitives; the token handling, validation and the request
 * are unchanged.
 */
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore, useAuthModal } from "@/features/auth/store";
import router from "@/router";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { FairButton } from "@/ui/primitives/FairButton";
import { FairIcon } from "@/ui/primitives/FairIcon";
import { FairInputText } from "@/ui/primitives/FairInputText";

const route = useRoute();
const toast = useToast();
const { t } = useI18n();

const password = ref("");
const passwordError = ref("");

const confirmPassword = ref("");
const confirmPasswordError = ref("");

const token = ref("");
const loadingState = ref(false);
const passwordVisible = ref(false);
const confirmVisible = ref(false);

const sixSymbolsValidation = computed(() => password.value.length >= 8);
const numberValidation = computed(() => /\d/.test(password.value));
const bigLetterValidation = computed(() => /\p{Lu}/u.test(password.value));
const smallLetterValidation = computed(() => /\p{Ll}/u.test(password.value));
const specialLetterValidation = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
);

const passwordValidations = computed(() => [
  { label: t("password_validation.min_6_chars"), validation: sixSymbolsValidation.value },
  { label: t("password_validation.one_number"), validation: numberValidation.value },
  {
    label: t("password_validation.one_uppercase"),
    validation: bigLetterValidation.value,
  },
  {
    label: t("password_validation.one_lowercase"),
    validation: smallLetterValidation.value,
  },
  {
    label: t("password_validation.one_special_char"),
    validation: specialLetterValidation.value,
  },
]);

const disableSubmit = computed(() => {
  return (
    password.value === "" ||
    confirmPassword.value === "" ||
    !!passwordError.value.length ||
    !!confirmPasswordError.value.length ||
    !sixSymbolsValidation.value ||
    !numberValidation.value ||
    !bigLetterValidation.value ||
    !smallLetterValidation.value ||
    !specialLetterValidation.value
  );
});

const validatePassword = () => {
  passwordError.value = password.value.length >= 8 ? "" : t("signup.password_error");
};

const validateConfirmPassword = () => {
  if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = t("signup.password_mismatch");
  } else {
    confirmPasswordError.value = "";
  }
};

async function setNewPassword() {
  loadingState.value = true;
  try {
    await useAuthStore().resetPassword({
      token: token.value,
      password: password.value,
    });

    showToast("Твой пароль успешно изменён");
    useAuthModal().hideAuthModal();
  } catch (error) {
    console.error("Password reset failed:", error);
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: "Не удалось изменить пароль. Попробуйте еще раз.",
      life: 5000,
    });
  } finally {
    loadingState.value = false;
  }
}

const showToast = (message: string) => {
  toast.add({ severity: "info", summary: "Info", detail: message, life: 3000 });
};

onMounted(() => {
  const { resetToken } = route.params as { resetToken?: string | string[] };
  if (Array.isArray(resetToken)) {
    token.value = resetToken[0] ?? "";
  } else {
    token.value = resetToken ?? "";
  }
  router.replace({ path: "/" });
});
</script>
<template>
  <form
    class="fair-auth-form"
    @submit.prevent="setNewPassword"
  >
    <div class="fair-auth-status">
      <h2 class="fair-auth-status__title">
        {{ t("reset_password.title") }}
      </h2>
    </div>

    <div class="fair-auth-form__field">
      <FairInputText :invalid="!!passwordError">
        <input
          id="auth-new-password"
          v-model="password"
          class="fair-input-text__control fair-text-input"
          :type="passwordVisible ? 'text' : 'password'"
          :placeholder="t('signup.password_label')"
          autocomplete="new-password"
          @input="validatePassword"
          @blur="validatePassword"
        >
        <button
          type="button"
          class="fair-auth-form__reveal"
          :aria-label="passwordVisible ? t('signup.hide_password') : t('signup.show_password')"
          @click="passwordVisible = !passwordVisible"
        >
          <FairIcon
            :name="passwordVisible ? 'hide' : 'show'"
            :size="20"
            color="currentColor"
          />
        </button>
      </FairInputText>
      <p
        v-if="passwordError"
        class="fair-auth-form__error"
      >
        {{ passwordError }}
      </p>
      <ul
        v-if="password"
        class="fair-auth-rules"
      >
        <li
          v-for="rule in passwordValidations"
          :key="rule.label"
          :class="['fair-auth-rules__item', rule.validation ? 'fair-auth-rules__item--met' : '']"
        >
          {{ rule.label }}
        </li>
      </ul>
    </div>

    <div class="fair-auth-form__field">
      <FairInputText :invalid="!!confirmPasswordError">
        <input
          id="auth-new-password-confirm"
          v-model="confirmPassword"
          class="fair-input-text__control fair-text-input"
          :type="confirmVisible ? 'text' : 'password'"
          :placeholder="t('signup.confirm_password_label')"
          autocomplete="new-password"
          @input="validateConfirmPassword"
          @blur="validateConfirmPassword"
        >
        <button
          type="button"
          class="fair-auth-form__reveal"
          :aria-label="confirmVisible ? t('signup.hide_password') : t('signup.show_password')"
          @click="confirmVisible = !confirmVisible"
        >
          <FairIcon
            :name="confirmVisible ? 'hide' : 'show'"
            :size="20"
            color="currentColor"
          />
        </button>
      </FairInputText>
      <p
        v-if="confirmPasswordError"
        class="fair-auth-form__error"
      >
        {{ confirmPasswordError }}
      </p>
    </div>

    <FairButton
      class="fair-auth-form__submit"
      type="submit"
      variant="primary"
      size="m"
      :rounded="false"
      :label="t('reset_password.submit_button')"
      :disabled="disableSubmit || loadingState"
    />
  </form>
</template>
